import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
/* eslint-disable no-unused-vars */
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Star,
  MapPin,
  Calendar,
  FileText,
  User,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { processAgentNotes } from '../../../utils/itineraryDataProcessor';
import { formatDate } from '../../../utils/browserCompatibility';

const QuestionnaireSummaryModal = ({
  isOpen,
  onClose,
  trip,
  itinerary,
  client
}) => {
  const [agentNotes, setAgentNotes] = useState({ sections: [], summary: "" });
  const [loading, setLoading] = useState(false);
  const [questionnaireData, setQuestionnaireData] = useState(null);
  const modalRef = useRef(null);

  // Focus management for accessibility
  useEffect(() => {
    if (isOpen) {
      const el = modalRef.current;
      el?.focus();
      const onKey = (e) => {
        if (e.key === "Escape") onClose();
        if (e.key === "Tab") {
          const focusables = el.querySelectorAll(
            'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
          );
          const list = Array.from(focusables);
          if (list.length === 0) return;
          const first = list[0];
          const last = list[list.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      el?.addEventListener("keydown", onKey);
      return () => el?.removeEventListener("keydown", onKey);
    }
  }, [isOpen, onClose]);

  // Fetch questionnaire data when modal opens
  useEffect(() => {
    if (isOpen && trip?.tripid) {
      setLoading(true);
      fetchQuestionnaireData();
    }
  }, [isOpen, trip?.tripid]);

  const fetchQuestionnaireData = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/itineraries/${trip.tripid}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const raw = await response.json();
        // API returns { status, data: { itinerary, QuestionnaireData, TripName, ... } } - unwrap
        const payload = raw?.data || raw;
        const itineraryObj = payload?.itinerary || payload?.ItineraryData || payload;
        // Questionnaire answers: top-level from DB (QuestionnaireData) or nested in itinerarydata
        const qData = payload?.QuestionnaireData ?? payload?.questionnaireData ?? itineraryObj?.questionnaireData ?? itineraryObj?.QuestionnaireData;

        // Merge for processAgentNotes: itinerary + questionnaireData (Prod/QA both use same shape)
        const mergedForNotes = {
          ...itineraryObj,
          questionnaireData: qData || itineraryObj?.questionnaireData || itineraryObj?.QuestionnaireData,
          tripSummary: itineraryObj?.TripSummary || itineraryObj?.tripSummary,
          DailyItineraries: itineraryObj?.DailyItineraries || itineraryObj?.dailyItineraries,
        };

        const notes = processAgentNotes(mergedForNotes);
        setAgentNotes(notes);
        setQuestionnaireData(qData || null);
      } else {
        setQuestionnaireData(null);
        setAgentNotes({ sections: [], summary: "" });
      }
    } catch (error) {
      console.error('Error fetching questionnaire data:', error);
      setQuestionnaireData(null);
      setAgentNotes({ sections: [], summary: "" });
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const getTripStatus = (startDate, endDate) => {
    if (!startDate || !endDate) return "Planning";

    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (today < start) {
      return "Planning";
    } else if (today >= start && today <= end) {
      return "In Progress";
    } else {
      return "Completed";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Planning":
        return "text-blue-600";
      case "In Progress":
        return "text-green-600";
      case "Completed":
        return "text-gray-600";
      default:
        return "text-gray-600";
    }
  };

  const getTotalGuests = () => {
    const adults = parseInt(questionnaireData?.groupInfo?.adults || "0", 10);
    const kids = parseInt(questionnaireData?.groupInfo?.kids || "0", 10);
    return adults + kids;
  };

  const getAdultCount = () => {
    return questionnaireData?.groupInfo?.adults || "0";
  };

  const getKidCount = () => {
    return parseInt(questionnaireData?.groupInfo?.kids || "0", 10);
  };

  const getAdultMalesCount = () => {
    return questionnaireData?.groupInfo?.numAdultMales || "0";
  };

  const getAdultFemalesCount = () => {
    return questionnaireData?.groupInfo?.numAdultFemales || "0";
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div className="flex fixed inset-0 z-modal justify-center items-center px-4 py-8 pt-16 pb-8 bg-black/40 sm:px-6" aria-hidden={!isOpen}>
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="tripNotesTitle"
          tabIndex={-1}
          className="bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl outline-none border border-gray-200 relative"
        >
          {/* Header */}
          <div className="flex justify-between items-center px-6 py-4 bg-white border-b border-gray-200">
            <div className="flex gap-2 items-center">
              <div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-full">
                <Star className="w-4 h-4 text-blue-600" />
              </div>
              <h2
                id="tripNotesTitle"
                className="text-xl font-bold text-gray-900"
              >
                Questionnaire Summary
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 rounded-full transition-colors hover:text-gray-600 hover:bg-gray-100"
              aria-label="Close modal"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="p-6 space-y-6">
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="flex gap-2 items-center text-gray-500">
                    <Clock className="w-4 h-4 animate-spin" />
                    <span>Loading trip notes...</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* Client Information Cards */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="flex gap-3 items-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-full">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">Client Name</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {client?.fullname || trip?.fullname || "Guest"}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3 items-center p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex justify-center items-center w-8 h-8 bg-green-100 rounded-full">
                        <MapPin className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">Destination</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {trip?.destination === 'disney_world' ? 'Disney World' :
                            trip?.destination === 'disneyland' ? 'Disneyland Resort' :
                              trip?.destination === 'universal_orlando' ? 'Universal Orlando' :
                                trip?.destination === 'disney_cruise' ? 'Disney Cruise Line' :
                                  'Disney World'}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3 items-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex justify-center items-center w-8 h-8 bg-purple-100 rounded-full">
                        <Calendar className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">Trip Dates</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {trip?.startdate && trip?.enddate
                            ? `${formatDate(trip.startdate)} – ${formatDate(trip.enddate)}`
                            : 'Dates not specified'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Trip Summary */}
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <div className="flex gap-2 items-center mb-3">
                      <div className="flex justify-center items-center w-8 h-8 bg-gray-100 rounded-full">
                        <FileText className="w-4 h-4 text-gray-600" />
                      </div>
                      <h3 className="text-base font-semibold text-gray-900">Trip Summary</h3>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-3">
                        <p><span className="font-medium text-gray-700">Trip Name:</span> {trip?.tripname || trip?.name || 'Untitled Trip'}</p>
                        <p><span className="font-medium text-gray-700">Status:</span>
                          <span className={`ml-1 font-medium ${getStatusColor(getTripStatus(trip?.startdate, trip?.enddate))}`}>
                            {getTripStatus(trip?.startdate, trip?.enddate)}
                          </span>
                        </p>
                      </div>

                      {/* Group Information */}
                      {questionnaireData?.groupInfo && (
                        <div className="pt-2 border-t border-gray-200">
                          <p className="mb-1 text-xs font-medium tracking-wide text-gray-700 uppercase">Group Information</p>
                          <div className="grid grid-cols-2 gap-3">
                            <p><span className="font-medium text-gray-700">Family Size:</span> {getTotalGuests()}</p>
                            <p><span className="font-medium text-gray-700">Adults:</span> {getAdultCount()}</p>
                            {getKidCount() > 0 && (
                              <p><span className="font-medium text-gray-700">Kids:</span> {getKidCount()}</p>
                            )}
                            {(getAdultMalesCount() !== "0" || getAdultFemalesCount() !== "0") && (
                              <>
                                <p><span className="font-medium text-gray-700">Adult Male(s):</span> {getAdultMalesCount()}</p>
                                <p><span className="font-medium text-gray-700">Adult Female(s):</span> {getAdultFemalesCount()}</p>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Enhanced Agent Notes */}
                  {agentNotes.sections && agentNotes.sections.length > 0 ? (
                    <div className="space-y-3">
                      {agentNotes.sections.map((section, sectionIdx) => (
                        <div key={sectionIdx} className="p-3 bg-white rounded-lg border border-gray-200">
                          <div className="mb-2">
                            <h3 className="text-base font-semibold text-gray-900">{section.title}</h3>
                            {section.subtitle && (
                              <p className="text-xs text-gray-500">{section.subtitle}</p>
                            )}
                          </div>

                          <div className="space-y-2">
                            {section.items.map((item, itemIdx) => (
                              <div key={itemIdx} className="flex justify-between items-start px-3 py-2 bg-gray-50 rounded border border-gray-200">
                                <span className="text-xs font-medium text-gray-700">
                                  {item.label}:
                                </span>
                                <div className="flex gap-2 items-center">
                                  <span className="max-w-xs text-xs text-right text-gray-600">
                                    {item.value}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}

                      {agentNotes.summary && (
                        <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                          <div className="flex gap-2 items-center mb-1">
                            <span className="text-xs font-semibold tracking-wide text-blue-700 uppercase">Quick Summary</span>
                          </div>
                          <p className="text-xs font-medium text-blue-600">{agentNotes.summary}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <div className="flex gap-2 items-center mb-2">
                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                        <h3 className="text-base font-semibold text-yellow-800">No Questionnaire Data Available</h3>
                      </div>
                      <p className="text-sm text-yellow-700">
                        This trip doesn't have questionnaire data yet. The client needs to complete the questionnaire form to see detailed preferences and notes here.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-white border-t border-gray-200">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg shadow-sm transition-colors hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default QuestionnaireSummaryModal;